import React from 'react';
import { Box, Typography, Card, CardActionArea, Skeleton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { remCalc } from '@monorepo/shared/src/util/style-util';
import { faBookReader, faShapes } from '@fortawesome/free-solid-svg-icons';
import IconBadge from 'components/common/IconBadge';
import theme, { FONT_GSS_FUTURA_SEMI_BOLD } from '@monorepo/shared/src/themes/defaultTheme';
import { getLetters } from '@monorepo/shared/src/util/text-util';
import useReports from 'state/hooks/use-reports';
import { CharacterProgress } from 'state/providers/ReportsProvider/types';
import { computePercent } from 'util/math-util';
import { Character } from '@monorepo/shared/src/graph/schema-generated-types-educator';

type Props = {
  character: Character;
};

const CharacterCard: React.FC<Props> = ({ character }) => {
  const classes = useStyles();
  const [{ charactersProgress, filteredStudents, isLoading }, { setQuery }] = useReports();

  const cardText = getLetters(character.character);
  const characterProgress = charactersProgress?.find((cp) => cp.characterId === character.id);

  const renderProgress = (characterProgress?: CharacterProgress) => {
    if (!characterProgress) return null; //TODO: Do something else here?
    return (
      <>
        {characterProgress.bookReadStudentCount !== undefined && (
          <IconBadge icon={faBookReader} color={theme.palette.link.light}>
            <Typography variant="caption" className={classes.badgeText}>
              {computePercent(characterProgress.bookReadStudentCount, filteredStudents.length)}%
            </Typography>
          </IconBadge>
        )}
        {characterProgress.activityCompletedCount !== undefined &&
          characterProgress.activityTotalCount !== undefined && (
            <IconBadge icon={faShapes} color={theme.palette.secondary.dark}>
              <Typography variant="caption" className={classes.badgeText}>
                {computePercent(
                  characterProgress.activityCompletedCount,
                  characterProgress.activityTotalCount
                )}
                %
              </Typography>
            </IconBadge>
          )}
      </>
    );
  };

  return (
    <Card>
      <CardActionArea onClick={() => setQuery({ character: character.character }, 'pushIn')}>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          margin={`${remCalc(12)} ${remCalc(20)}`}
          height={114}
        >
          <Box display="flex" justifyContent="space-between">
            <Typography variant="h5" className={classes.cardText}>
              {cardText}
            </Typography>
          </Box>
          <Box display="flex" columnGap={remCalc(35)}>
            {isLoading ? (
              <Skeleton className={classes.skeleton} />
            ) : (
              renderProgress(characterProgress)
            )}
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
};

const useStyles = makeStyles(() => ({
  cardText: {
    fontSize: remCalc(24),
  },
  badgeText: {
    fontSize: remCalc(14),
    lineHeight: remCalc(24),
    fontWeight: 400,
    fontFamily: FONT_GSS_FUTURA_SEMI_BOLD,
  },
  skeleton: {
    width: '100%',
    height: remCalc(48),
  },
}));

export default CharacterCard;
